import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { Link } from 'react-router-dom';
import { Button, Callout, Spinner } from '@blueprintjs/core';
import { useQuery } from '@tanstack/react-query';
import { AgGridReact } from 'ag-grid-react';
import type { ColDef, Column, GridApi, ICellRendererParams, IHeaderParams, RowClassParams } from 'ag-grid-community';
import 'ag-grid-enterprise';
import 'ag-grid-community/styles/ag-grid.css';
import 'ag-grid-community/styles/ag-theme-alpine.css';
import './FinancialReports.css';
import { queryPolicies } from '../../../utils/queryPolicies';
import { useMyCapabilities } from '../../../utils/metadata/capabilities';
import { DATE_RANGE_PRESETS } from '../../ui/DateRangeSelect';
import { presetToDateRange } from '../../DashboardPage/dashboardFilterHelpers';
import { fetchFinancialFilterOptions, fetchMonthly, FINANCIAL_REPORT_QUERY_KEY, type DashboardFilters, type DashboardScope, type DateRange,
  type MonthlyRow, type MonthlySection } from './financialReportsApi';

const DEFAULT_PRESET = 'this_year';
// The standard presets plus whole past calendar years (2024 onwards).
const currentYear = new Date().getFullYear();
const PAST_YEARS = Array.from({ length: Math.max(currentYear - 2024, 0) }, (_, index) => currentYear - 1 - index);
const CUSTOM_PRESET = 'custom';
const PERIOD_OPTIONS = [...DATE_RANGE_PRESETS, ...PAST_YEARS.map((year) => ({ value: `year_${year}`, label: String(year) })),
  { value: CUSTOM_PRESET, label: 'Custom range' }];
const periodRange = (preset: string) => {
  const year = /^year_(\d{4})$/.exec(preset)?.[1];
  return year ? { start: `${year}-01-01`, end: `${year}-12-31` } : presetToDateRange(preset);
};
// Calendar arithmetic on YYYY-MM-DD strings, in UTC so no time zone shifts a day.
const toDate = (value: string) => new Date(`${value}T00:00:00Z`);
const toIso = (date: Date) => date.toISOString().slice(0, 10);
const addDays = (value: string, days: number) => { const date = toDate(value); date.setUTCDate(date.getUTCDate() + days); return toIso(date); };
/** Same day `months` earlier, kept inside that month (Mar 31 minus one month is Feb 28). */
function addMonths(value: string, months: number): string {
  const date = toDate(value);
  const day = date.getUTCDate();
  date.setUTCDate(1);
  date.setUTCMonth(date.getUTCMonth() + months);
  const last = new Date(Date.UTC(date.getUTCFullYear(), date.getUTCMonth() + 1, 0)).getUTCDate();
  date.setUTCDate(Math.min(day, last));
  return toIso(date);
}
/**
 * The period the KPI changes compare against: the same days one calendar period
 * back for this week / month / quarter / year and whole past years, otherwise the
 * equally long stretch just before. All time has nothing to compare with.
 */
export function previousRange(preset: string, range: DateRange): DateRange | null {
  if (!range.start || !range.end) return null;
  const back = ({ this_week: [0, 7], this_month: [1, 0], this_quarter: [3, 0], this_year: [12, 0] } as Record<string, [number, number]>)[preset]
    ?? (/^year_\d{4}$/.test(preset) ? [12, 0] : null);
  if (back) {
    const [months, days] = back;
    return { start: addDays(addMonths(range.start, -months), -days), end: addDays(addMonths(range.end, -months), -days) };
  }
  const length = Math.round((toDate(range.end).getTime() - toDate(range.start).getTime()) / 86_400_000);
  const end = addDays(range.start, -1);
  return { start: addDays(end, -length), end };
}
const shortDate = (value: string, withYear = true) => toDate(value).toLocaleDateString('en-US',
  { month: 'short', day: 'numeric', ...(withYear ? { year: 'numeric' } : {}), timeZone: 'UTC' });
export function describeRange(range: DateRange): string {
  if (!range.start && !range.end) return 'All time';
  if (!range.start) return `Through ${shortDate(range.end!)}`;
  if (!range.end) return `From ${shortDate(range.start)}`;
  if (range.start === range.end) return shortDate(range.start);
  const sameYear = range.start.slice(0, 4) === range.end.slice(0, 4);
  return `${shortDate(range.start, !sameYear)} - ${shortDate(range.end)}`;
}
const money = new Intl.NumberFormat('en-US', { style: 'currency', currency: 'USD', minimumFractionDigits: 2 });
const wholeMoney = new Intl.NumberFormat('en-US', { style: 'currency', currency: 'USD', maximumFractionDigits: 0 });
const percent = new Intl.NumberFormat('en-US', { style: 'percent', maximumFractionDigits: 1 });
const labelMonth = (value: string) => new Date(`${value}T12:00:00Z`).toLocaleDateString('en-US', { month: 'long', year: 'numeric', timeZone: 'UTC' });
const monthNumber = (value: unknown) => typeof value === 'string' && /^\d{4}-\d{2}/.test(value) ? Number(value.slice(5, 7)) : null;
const ratio = (part: number, whole: number) => whole ? part / whole : null;
const MONEY = 'fd-money', COUNT = 'fd-count', RATE = 'fd-rate';
const GROUP_MIN_WIDTH = 165;
const columnKind = (column: Column) => {
  const classes = column.getColDef().cellClass;
  return [MONEY, COUNT, RATE].find((kind) => Array.isArray(classes) && classes.includes(kind));
};

interface SelectionTotals { count: number; numbers: number; sum: number; min: number; max: number; money: boolean }
/** Sheet-style arithmetic for the selected cells: $ on money columns, plain numbers for units. */
function selectionTotals(grid: GridApi<MonthlyRow>): SelectionTotals | null {
  let count = 0, sum = 0, min = Infinity, max = -Infinity, money = true, numbers = 0;
  for (const range of grid.getCellRanges() ?? []) {
    if (!range.startRow || !range.endRow || range.startRow.rowPinned) continue;
    const [from, to] = [range.startRow.rowIndex, range.endRow.rowIndex].sort((a, b) => a - b);
    for (let index = from; index <= to; index += 1) {
      // An expanded month's own row would double count its children.
      const rowNode = grid.getDisplayedRowAtIndex(index);
      if (!rowNode || (rowNode.group && rowNode.expanded)) continue;
      for (const column of range.columns) {
        count += 1;
        const kind = columnKind(column);
        if (kind !== MONEY && kind !== COUNT) continue;
        const value = Number(grid.getCellValue({ rowNode, colKey: column }));
        if (!Number.isFinite(value)) continue;
        numbers += 1; sum += value; min = Math.min(min, value); max = Math.max(max, value);
        if (kind !== MONEY) money = false;
      }
    }
  }
  return count > 1 && numbers ? { count, numbers, sum, min, max, money } : null;
}
// Pinned to the bottom of the window like Sheets, so it is visible wherever the selected table is.
function SelectionBar({ totals }: { totals: SelectionTotals | null }): React.ReactElement | null {
  if (!totals) return null;
  const format = (value: number) => totals.money ? money.format(value) : value.toLocaleString('en-US', { maximumFractionDigits: 2 });
  return <div className="fd-totals" role="status" aria-label="Selection totals">
    <span>Sum <strong>{format(totals.sum)}</strong></span><span>Average <strong>{format(totals.sum / totals.numbers)}</strong></span>
    <span>Count <strong>{totals.count}</strong></span><span>Min <strong>{format(totals.min)}</strong></span><span>Max <strong>{format(totals.max)}</strong></span>
  </div>;
}
type TotalField = 'deal_count' | 'funded_amount' | 'gross' | 'commission' | 'psf' | 'david_commission' | 'zack_commission' | 'other_commission'
  | 'application_count' | 'approval_count' | 'approved_amount';

/** Header: the arrow sorts, the name selects the whole column so the selection bar shows its totals. */
function SelectableHeader(props: IHeaderParams<MonthlyRow>): React.ReactElement {
  const [sort, setSort] = useState(props.column.getSort());
  useEffect(() => {
    const update = () => setSort(props.column.getSort());
    props.column.addEventListener('sortChanged', update);
    return () => props.column.removeEventListener('sortChanged', update);
  }, [props.column]);
  const selectColumn = () => {
    const last = props.api.getDisplayedRowCount() - 1;
    props.api.clearCellSelection();
    if (last >= 0) props.api.addCellRange({ rowStartIndex: 0, rowEndIndex: last, columnStart: props.column, columnEnd: props.column });
  };
  const numeric = props.column.getColDef().type === 'numericColumn';
  return <div className={`fd-header${numeric ? ' fd-header--numeric' : ''}`}>
    <button type="button" className="fd-header-label" title="Select this column" onClick={selectColumn}>{props.displayName}</button>
    {props.enableSorting && <button type="button" className={`fd-header-sort${sort ? ' is-active' : ''}`} aria-label={`Sort by ${props.displayName}`}
      onClick={(event) => props.progressSort(event.shiftKey)}>{sort === 'asc' ? '▲' : sort === 'desc' ? '▼' : '↕'}</button>}
  </div>;
}

function EntityCell({ value, node, showTotal }: ICellRendererParams<MonthlyRow> & { showTotal?: boolean }): React.ReactElement | null {
  if (node.group) return null;
  if (node.rowPinned) return showTotal ? <strong>Total</strong> : null;
  return <span>{value ? String(value) : 'Unassigned'}</span>;
}
// Columns flex to fill the card, which is as wide as the page; the minimums keep money readable.
function numberColumn(field: TotalField, label: string, currency = false, aggFunc = 'sum'): ColDef<MonthlyRow> {
  return { field, headerName: label, aggFunc, type: 'numericColumn', flex: currency ? 1.3 : 1, minWidth: currency ? 130 : 90,
    cellClass: [currency ? MONEY : COUNT, 'ag-right-aligned-cell'],
    valueFormatter: ({ value }) => value == null ? '' : currency ? money.format(Number(value)) : Number(value).toLocaleString('en-US'),
  };
}
function rateColumn(colId: string, headerName: string, part: TotalField, whole: TotalField): ColDef<MonthlyRow> {
  return { colId, headerName, type: 'numericColumn', flex: 1, minWidth: 90, cellClass: [RATE, 'ag-right-aligned-cell'],
    valueGetter: ({ data }) => data ? ratio(Number(data[part] ?? 0), Number(data[whole] ?? 0)) : null,
    valueFormatter: ({ value }) => value == null ? '' : percent.format(Number(value)) };
}
const monthColumn: ColDef<MonthlyRow> = { field: 'month', headerName: 'Month', flex: 1.2, minWidth: 140, sort: 'desc', valueFormatter: ({ value, node }) => node?.rowPinned ? 'Total' : value ? labelMonth(value) : '' };
const monthGroup: ColDef<MonthlyRow> = { field: 'month', rowGroup: true, hide: true, sort: 'desc', valueFormatter: ({ value }) => value ? labelMonth(value) : '' };
const entity = (headerName: string, showTotal = false): ColDef<MonthlyRow> => ({ field: 'entity_label', headerName, flex: 2, minWidth: 170, cellRenderer: EntityCell, cellRendererParams: { showTotal } });
const breakdownColumns = (label: string, grouped: boolean): ColDef<MonthlyRow>[] => [...(grouped ? [monthGroup] : []), entity(label, !grouped),
  numberColumn('deal_count', 'Deals'), numberColumn('funded_amount', 'Funded Amount', true), numberColumn('gross', 'Gross Commission', true)];
const BREAKDOWN_TOTALS: TotalField[] = ['deal_count', 'funded_amount', 'gross'];

interface TableSpec { section: MonthlySection; title: string; subtitle: string; columns: ColDef<MonthlyRow>[]; totals: TotalField[] }
// Each inner array is one row of a section; paired tables sit side by side on wide screens.
const layout: TableSpec[][] = [
  [{ section: 'business', title: 'Business Level', subtitle: 'Every funded deal (clawbacks included) and the commission split, by funding month.',
    columns: [monthColumn, numberColumn('deal_count', 'Deals'), numberColumn('funded_amount', 'Funded Amount', true),
      numberColumn('gross', 'Gross Commission', true), numberColumn('david_commission', 'David’s Commission', true),
      numberColumn('zack_commission', 'Zack’s Commission', true), numberColumn('other_commission', 'Other Reps', true)],
    totals: ['deal_count', 'funded_amount', 'gross', 'david_commission', 'zack_commission', 'other_commission'] }],
  [{ section: 'pipeline', title: 'Applications & Approvals', subtitle: 'Unique applications and unique primary approvals (one per business), and the funded rate from approval to funded, by month.',
    columns: [monthColumn, numberColumn('application_count', 'Unique Apps'), numberColumn('approval_count', 'Approvals'),
      numberColumn('approved_amount', 'Approved Amount', true), rateColumn('approval_rate', 'Approval Rate', 'approval_count', 'application_count'),
      numberColumn('deal_count', 'Funded'), numberColumn('funded_amount', 'Funded Amount', true),
      rateColumn('funded_rate_units', 'Funded Rate (units)', 'deal_count', 'approval_count'), rateColumn('funded_rate_dollars', 'Funded Rate ($)', 'funded_amount', 'approved_amount')],
    totals: ['application_count', 'approval_count', 'approved_amount', 'deal_count', 'funded_amount'] }],
  // David and Zack are credited on every deal, so a month's units, funded and gross are the largest rep's (max), not a sum.
  [{ section: 'rep', title: 'By Rep', subtitle: 'Every rep on a funded deal: main rep or commissioned rep. Expand a month.',
    columns: [monthGroup, entity('Rep'), numberColumn('deal_count', 'Deals', false, 'max'), numberColumn('funded_amount', 'Funded Amount', true, 'max'),
      numberColumn('gross', 'Gross Commission', true, 'max'), numberColumn('commission', 'Rep Commission', true)],
    totals: ['commission'] }],
  [{ section: 'funder', title: 'By Funder', subtitle: 'Funded deals per funder. Expand a month.',
    columns: [monthGroup, entity('Funder'), numberColumn('deal_count', 'Deals'), numberColumn('funded_amount', 'Funded Amount', true),
      numberColumn('commission', 'Commission', true), numberColumn('psf', 'PSF', true), numberColumn('gross', 'Gross Commission', true)],
    totals: ['deal_count', 'funded_amount', 'commission', 'psf', 'gross'] }],
  [{ section: 'lead_source', title: 'By Lead Source', subtitle: 'Funded deals per lead source. Expand a month.', columns: breakdownColumns('Lead Source', true), totals: BREAKDOWN_TOTALS },
    { section: 'lead_source_total', title: 'Lead Source Totals', subtitle: 'The selected period.', columns: breakdownColumns('Lead Source', false), totals: BREAKDOWN_TOTALS }],
  [{ section: 'deal_type', title: 'Deal Type by Month', subtitle: 'Funded deals per deal type. Expand a month.', columns: breakdownColumns('Deal Type', true), totals: BREAKDOWN_TOTALS },
    { section: 'deal_type_total', title: 'Deal Type Totals', subtitle: 'The selected period.', columns: breakdownColumns('Deal Type', false), totals: BREAKDOWN_TOTALS }],
];

// The page folds into sections so it is not one long wall of tables (REG-969).
type SectionId = 'business' | 'reps' | 'funders' | 'lead_sources' | 'deal_types';
interface SectionSpec { id: SectionId; title: string; rows: TableSpec[][] }
const SECTIONS: SectionSpec[] = [
  { id: 'business', title: 'Business', rows: [layout[0], layout[1]] },
  { id: 'reps', title: 'Reps', rows: [layout[2]] },
  { id: 'funders', title: 'Funders', rows: [layout[3]] },
  { id: 'lead_sources', title: 'Lead Sources', rows: [layout[4]] },
  { id: 'deal_types', title: 'Deal Types', rows: [layout[5]] },
];
// A rep's Dashboard tab leaves out lead source (Zack, 10/28).
const REP_SECTIONS = SECTIONS.filter((section) => section.id !== 'lead_sources');
const DEFAULT_OPEN: SectionId[] = ['business', 'reps'];
const OPEN_STORAGE_KEY = 'funded-dashboard-open-sections';
function readOpenSections(): SectionId[] {
  try {
    const stored = JSON.parse(window.localStorage.getItem(OPEN_STORAGE_KEY) ?? 'null') as unknown;
    return Array.isArray(stored) ? stored.filter((id): id is SectionId => SECTIONS.some((section) => section.id === id)) : DEFAULT_OPEN;
  } catch {
    return DEFAULT_OPEN;
  }
}
function storeOpenSections(open: SectionId[]): void {
  try { window.localStorage.setItem(OPEN_STORAGE_KEY, JSON.stringify(open)); } catch { /* private window: not remembered */ }
}
const distinctEntities = (rows: MonthlyRow[], section: MonthlySection) => new Set(rows.filter((row) => row.section === section && !row.is_subtotal)
  .map((row) => row.entity_id ?? row.entity_label)).size;
function sectionSummary(id: SectionId, rows: MonthlyRow[]): string {
  const plural = (count: number, word: string) => `${count.toLocaleString('en-US')} ${word}${count === 1 ? '' : 's'}`;
  if (id === 'business') {
    const business = rows.filter((row) => row.section === 'business');
    const deals = business.reduce((sum, row) => sum + Number(row.deal_count ?? 0), 0);
    const funded = business.reduce((sum, row) => sum + Number(row.funded_amount ?? 0), 0);
    return `${plural(deals, 'deal')}, ${wholeMoney.format(funded)} funded`;
  }
  if (id === 'reps') return plural(distinctEntities(rows, 'rep'), 'rep');
  if (id === 'funders') return plural(distinctEntities(rows, 'funder'), 'funder');
  if (id === 'lead_sources') return plural(distinctEntities(rows, 'lead_source_total'), 'lead source');
  return plural(distinctEntities(rows, 'deal_type_total'), 'deal type');
}

// Two colours only: months (and a month's rows) alternate between the primary tint and the secondary tint.
function rowClass({ data, node }: RowClassParams<MonthlyRow>): string {
  if (node.rowPinned) return 'fd-row-total';
  const month = monthNumber(node.group ? node.allLeafChildren?.[0]?.data?.month : data?.month);
  const band = (month ?? (node.rowIndex ?? 0) + 1) % 2 === 0 ? 'fd-band-a' : 'fd-band-b';
  return `${band}${node.group ? ' fd-group' : ''}`;
}
type SelectionHandler = (api: GridApi<MonthlyRow>, totals: SelectionTotals | null) => void;
function MonthlyTable({ spec, rows, totalsOverride, onSelection }: { spec: TableSpec; rows: MonthlyRow[]; totalsOverride?: Partial<Record<TotalField, number>>; onSelection: SelectionHandler }) {
  const data = useMemo(() => rows.filter((row) => row.section === spec.section && (spec.section === 'business' || spec.section === 'pipeline' || !row.is_subtotal)), [rows, spec.section]);
  const pinned = useMemo(() => {
    if (!data.length) return [];
    const total: Record<string, unknown> = { section: spec.section, month: null, entity_id: null, entity_label: 'Total' };
    for (const field of spec.totals) total[field] = data.reduce((sum, row) => sum + Number(row[field] ?? 0), 0);
    return [{ ...total, ...totalsOverride } as unknown as MonthlyRow];
  }, [data, spec, totalsOverride]);
  return <section aria-label={spec.title} className="fd-card">
    <header className="fd-card-header"><h3>{spec.title}</h3><p>{spec.subtitle}</p></header>
    <div className="ag-theme-alpine financial-monthly-grid fd-grid">
      <AgGridReact<MonthlyRow> rowData={data} columnDefs={spec.columns} pinnedBottomRowData={pinned}
        defaultColDef={{ resizable: true, sortable: true, wrapHeaderText: true, autoHeaderHeight: true, headerComponent: SelectableHeader, suppressHeaderMenuButton: true, sortingOrder: ['desc', 'asc'] }}
        autoGroupColumnDef={{ headerName: 'Month', flex: 1.2, minWidth: GROUP_MIN_WIDTH, cellRendererParams: { suppressCount: true },
          valueFormatter: ({ value, node }) => node?.rowPinned ? 'Total' : value ? labelMonth(String(value)) : '' }}
        groupDefaultExpanded={0} suppressAggFuncInHeader animateRows={false} domLayout="autoHeight" rowHeight={38}
        getRowClass={rowClass} cellSelection
        onCellSelectionChanged={(event) => onSelection(event.api, selectionTotals(event.api))}
        onCellKeyDown={(event) => { if ((event.event as KeyboardEvent | undefined)?.key === 'Escape') event.api.clearCellSelection(); }}
        getRowId={({ data: row }) => `${row.month ?? 'all'}:${row.entity_id ?? row.entity_label ?? 'none'}`}
        overlayNoRowsTemplate="Nothing to show for this period." suppressCsvExport suppressExcelExport
        popupParent={document.body} />
    </div>
  </section>;
}
interface KpiTotals {
  funded: number; deals: number; gross: number; applications: number; approvals: number; approved: number;
  clawbackCommission: number; clawbackDeals: number;
}
function kpiTotals(rows: MonthlyRow[]): KpiTotals {
  const sum = (section: MonthlySection, field: TotalField) => rows.filter((row) => row.section === section).reduce((total, row) => total + Number(row[field] ?? 0), 0);
  const clawback = rows.find((row) => row.section === 'clawback');
  return { funded: sum('business', 'funded_amount'), deals: sum('business', 'deal_count'), gross: sum('business', 'gross'),
    applications: sum('pipeline', 'application_count'), approvals: sum('pipeline', 'approval_count'), approved: sum('pipeline', 'approved_amount'),
    clawbackCommission: Number(clawback?.commission ?? 0), clawbackDeals: Number(clawback?.deal_count ?? 0) };
}
interface KpiCard { label: string; value: string; note?: string; tone?: string; current: number | null; previous?: number | null; kind: 'amount' | 'rate'; lowerIsBetter?: boolean }
/** "+12.3%" for amounts and counts, "+2.1 pts" for rates; the period compared with is named once above the boxes. */
function KpiChange({ card, against }: { card: KpiCard; against: string }): React.ReactElement | null {
  if (card.current == null || card.previous == null) return null;
  const change = card.kind === 'rate' ? (card.current - card.previous) * 100 : card.previous ? (card.current - card.previous) / Math.abs(card.previous) : null;
  if (change == null || !Number.isFinite(change)) return card.current ? <span className="fd-change" title={`Nothing in ${against}`}>new</span> : null;
  const rounded = Math.round(change * (card.kind === 'rate' ? 10 : 1000)) / (card.kind === 'rate' ? 10 : 10);
  const direction = rounded > 0 ? 'up' : rounded < 0 ? 'down' : 'flat';
  const good = direction === 'flat' ? 'flat' : (direction === 'up') !== Boolean(card.lowerIsBetter) ? 'good' : 'bad';
  const text = card.kind === 'rate' ? `${rounded > 0 ? '+' : ''}${rounded.toFixed(1)} pts` : `${rounded > 0 ? '+' : ''}${rounded.toFixed(1)}%`;
  return <span className={`fd-change fd-change--${good}`} title={`Compared with ${against}`}>
    {direction === 'up' ? '▲' : direction === 'down' ? '▼' : '='} {text}
  </span>;
}
function KpiRow({ rows, previous, against }: { rows: MonthlyRow[]; previous?: MonthlyRow[]; against: string | null }) {
  const now = kpiTotals(rows);
  const before = previous && against ? kpiTotals(previous) : null;
  const rateOf = (part: number, whole: number) => ratio(part, whole);
  const rate = (value: number | null) => value == null ? '' : percent.format(value);
  const cards: KpiCard[] = [
    { label: 'Amount Funded', value: wholeMoney.format(now.funded), current: now.funded, previous: before?.funded, kind: 'amount' },
    { label: 'Deals Funded', value: now.deals.toLocaleString('en-US'), current: now.deals, previous: before?.deals, kind: 'amount' },
    { label: 'Total Commissions', value: wholeMoney.format(now.gross), current: now.gross, previous: before?.gross, kind: 'amount' },
    { label: 'Avg Deal Size', value: now.deals ? wholeMoney.format(now.funded / now.deals) : '', current: rateOf(now.funded, now.deals),
      previous: before ? rateOf(before.funded, before.deals) : null, kind: 'amount' },
    { label: 'Unique Apps', value: now.applications.toLocaleString('en-US'), current: now.applications, previous: before?.applications, kind: 'amount' },
    { label: 'Approval Amount', value: wholeMoney.format(now.approved), note: `${now.approvals.toLocaleString('en-US')} approvals`,
      current: now.approved, previous: before?.approved, kind: 'amount' },
    { label: 'Approval Rate', value: rate(rateOf(now.approvals, now.applications)), note: 'approvals / apps',
      current: rateOf(now.approvals, now.applications), previous: before ? rateOf(before.approvals, before.applications) : null, kind: 'rate' },
    { label: 'Funded Rate (units)', value: rate(rateOf(now.deals, now.approvals)), note: 'funded / approvals',
      current: rateOf(now.deals, now.approvals), previous: before ? rateOf(before.deals, before.approvals) : null, kind: 'rate' },
    { label: 'Funded Rate ($)', value: rate(rateOf(now.funded, now.approved)), note: 'funded $ / approved $',
      current: rateOf(now.funded, now.approved), previous: before ? rateOf(before.funded, before.approved) : null, kind: 'rate' },
    { label: 'Clawbacks', value: wholeMoney.format(now.clawbackCommission), note: `${now.clawbackDeals.toLocaleString('en-US')} deals`, tone: 'secondary',
      current: now.clawbackCommission, previous: before?.clawbackCommission, kind: 'amount', lowerIsBetter: true },
  ];
  return <><div className="financial-kpis" aria-label="Funded totals">
    {cards.map((card) => <div key={card.label} className={`financial-kpi${card.tone ? ` financial-kpi--${card.tone}` : ''}`}>
      <span>{card.label}</span><strong>{card.value}</strong>{card.note && <em>{card.note}</em>}
      {before && against && <KpiChange card={card} against={against} />}
    </div>)}
  </div>
  {before && against && <p className="fd-compare">Changes compare with {against}.</p>}</>;
}
type FilterKey = 'rep' | 'funder' | 'leadSource';
const FILTERS: Array<{ key: FilterKey; label: string; scope: DashboardScope['kind']; kinds: string[] }> = [
  { key: 'rep', label: 'Rep', scope: 'rep', kinds: ['main_rep', 'rep'] },
  { key: 'funder', label: 'Funder', scope: 'funder', kinds: ['funder'] },
  { key: 'leadSource', label: 'Lead Source', scope: 'lead_source', kinds: ['lead_source'] },
];
/** Company dashboard, or (with `scope`) the same tables for one rep, funder or lead source. Live: no snapshot or refresh. */
export default function FundedDashboard({ embedded = false, scope }: { embedded?: boolean; scope?: DashboardScope }): React.ReactElement | null {
  const { hasCapability, isLoading: capabilitiesLoading } = useMyCapabilities();
  const [preset, setPreset] = useState(DEFAULT_PRESET);
  const [custom, setCustom] = useState<DateRange>(() => periodRange(DEFAULT_PRESET));
  const [filters, setFilters] = useState<DashboardFilters>({});
  const range = useMemo(() => preset === CUSTOM_PRESET ? custom : periodRange(preset), [custom, preset]);
  const before = useMemo(() => previousRange(preset, range), [preset, range]);
  const canView = hasCapability('edit_permissions');
  const result = useQuery({ queryKey: [...FINANCIAL_REPORT_QUERY_KEY, 'monthly', range, scope ?? null, filters], queryFn: () => fetchMonthly(range, scope, filters),
    enabled: canView, placeholderData: (previous) => previous, ...queryPolicies.dashboardAggregate, refetchOnWindowFocus: true });
  const previousResult = useQuery({ queryKey: [...FINANCIAL_REPORT_QUERY_KEY, 'monthly', before, scope ?? null, filters], queryFn: () => fetchMonthly(before!, scope, filters),
    enabled: canView && Boolean(before), ...queryPolicies.dashboardAggregate });
  const options = useQuery({ queryKey: [...FINANCIAL_REPORT_QUERY_KEY, 'filters'], queryFn: fetchFinancialFilterOptions, enabled: canView, ...queryPolicies.dashboardAggregate });
  const choices = useMemo(() => Object.fromEntries(FILTERS.map((filter) => {
    const byId = new Map<number, string>();
    for (const option of options.data ?? []) {
      if (filter.kinds.includes(option.kind) && option.id != null && !byId.has(option.id)) byId.set(option.id, option.label ?? `#${option.id}`);
    }
    return [filter.key, [...byId].sort((a, b) => a[1].localeCompare(b[1]))];
  })) as Record<FilterKey, Array<[number, string]>>, [options.data]);
  const narrowed = Object.values(filters).some((value) => value != null);
  const filtered = narrowed || preset !== DEFAULT_PRESET;
  // The By Rep total row uses company totals: David and Zack are on every deal, so summing reps would double count.
  const repTotals = useMemo(() => {
    const business = (result.data ?? []).filter((row) => row.section === 'business');
    const total = (field: TotalField) => business.reduce((sum, row) => sum + Number(row[field] ?? 0), 0);
    return { deal_count: total('deal_count'), funded_amount: total('funded_amount'), gross: total('gross') };
  }, [result.data]);
  const [open, setOpen] = useState<SectionId[]>(readOpenSections);
  const toggle = (id: SectionId) => setOpen((current) => {
    const next = current.includes(id) ? current.filter((value) => value !== id) : [...current, id];
    storeOpenSections(next);
    return next;
  });
  const sections = scope?.kind === 'rep' ? REP_SECTIONS : SECTIONS;
  const setAll = (expand: boolean) => { const next = expand ? sections.map((section) => section.id) : []; storeOpenSections(next); setOpen(next); };
  // One selection at a time across the tables: selecting in one table clears the previous one.
  const [selection, setSelection] = useState<SelectionTotals | null>(null);
  const activeGrid = useRef<GridApi<MonthlyRow> | null>(null);
  const onSelection = useCallback<SelectionHandler>((api, totals) => {
    if (totals && activeGrid.current && activeGrid.current !== api && !activeGrid.current.isDestroyed()) activeGrid.current.clearCellSelection();
    if (totals) activeGrid.current = api;
    if (totals || activeGrid.current === api) setSelection(totals);
  }, []);
  // A closed section drops its grid, and new numbers replace the cells: either way the selection goes.
  useEffect(() => {
    if (activeGrid.current && !activeGrid.current.isDestroyed()) activeGrid.current.clearCellSelection();
    activeGrid.current = null;
    setSelection(null);
  }, [open, range, filters]);
  if (capabilitiesLoading) return <Spinner aria-label="Loading permissions" />;
  if (!canView) return scope ? null : <><Link to="/admin/reporting">Reports</Link><Callout intent="warning">The Funded Dashboard is available to the Exec and Dev groups only.</Callout></>;
  const rows = result.data ?? [];
  const noDeals = !result.isPending && !result.error && !rows.some((row) => row.section === 'business' && Number(row.deal_count ?? 0) > 0);
  const against = before ? describeRange(before) : null;
  return <div className="fd">
    {!embedded && <Link to="/admin/reporting">Reports</Link>}
    {!scope && <div className="fd-title">{embedded ? <h3>Funded Dashboard</h3> : <h2>Funded Dashboard</h2>}</div>}
    <div className="financial-report-filters fd-filters" aria-label="Dashboard filters">
      <label>Funding date
        <select aria-label="Funding date" value={preset} onChange={(event) => {
          if (event.target.value === CUSTOM_PRESET) setCustom(range);
          setPreset(event.target.value);
        }}>
          {PERIOD_OPTIONS.map((option) => <option key={option.value} value={option.value}>{option.label}</option>)}
        </select></label>
      {preset === CUSTOM_PRESET && <>
        <label>From<input type="date" aria-label="Funded from" value={custom.start ?? ''} max={custom.end ?? undefined}
          onChange={(event) => setCustom((current) => ({ ...current, start: event.target.value || null }))} /></label>
        <label>Through<input type="date" aria-label="Funded through" value={custom.end ?? ''} min={custom.start ?? undefined}
          onChange={(event) => setCustom((current) => ({ ...current, end: event.target.value || null }))} /></label>
      </>}
      {FILTERS.filter((filter) => filter.scope !== scope?.kind).map((filter) => <label key={filter.key}>{filter.label}
        <select aria-label={filter.label} value={filters[filter.key] ?? ''}
          onChange={(event) => setFilters((current) => ({ ...current, [filter.key]: event.target.value ? Number(event.target.value) : null }))}>
          <option value="">All</option>
          {choices[filter.key].map(([id, label]) => <option key={id} value={id}>{label}</option>)}
        </select></label>)}
      {filtered && <Button minimal small icon="cross" onClick={() => { setPreset(DEFAULT_PRESET); setFilters({}); }}>Clear</Button>}
      <span className="fd-range" aria-label="Showing">{describeRange(range)}</span>
      {result.isFetching && !result.isPending && <Spinner size={14} aria-label="Updating" />}
    </div>
    {options.error && <Callout intent="warning">Rep, funder and lead source choices did not load. <Button small onClick={() => void options.refetch()}>Retry</Button></Callout>}
    {result.isPending ? <Spinner aria-label="Loading monthly reports" /> : result.error ?
      <Callout intent="danger">{result.error.message} <Button onClick={() => void result.refetch()}>Retry</Button></Callout> :
      <><KpiRow rows={rows} previous={previousResult.data} against={against} />
        {noDeals && <Callout intent="primary" icon="calendar" className="fd-empty">No funded deals in {describeRange(range)}{narrowed ? ' with these filters' : ''}.</Callout>}
        <div className="fd-section-tools">
          <p className="financial-report-note">Live numbers, newest month first. Funded includes clawed-back deals; the Clawbacks box shows how much of that was clawed back. Click a column name to select it; click the arrow to sort. Drag across cells for Sum, Average, Count, Min and Max.</p>
          <span><Button minimal small icon="expand-all" onClick={() => setAll(true)}>Expand all</Button>
            <Button minimal small icon="collapse-all" onClick={() => setAll(false)}>Collapse all</Button></span>
        </div>
        <div className="financial-monthly-tables">
          {sections.map((section) => {
            const isOpen = open.includes(section.id);
            return <div key={section.id} className={`fd-section${isOpen ? ' is-open' : ''}`}>
              <button type="button" className="fd-section-header" aria-expanded={isOpen} aria-controls={`fd-section-${section.id}`} onClick={() => toggle(section.id)}>
                <span className="fd-section-chevron" aria-hidden="true">{isOpen ? '▾' : '▸'}</span>
                <span className="fd-section-title">{section.title}</span>
                <span className="fd-section-summary">{sectionSummary(section.id, rows)}</span>
              </button>
              {isOpen && <div id={`fd-section-${section.id}`} className="fd-section-body">
                {section.rows.map((pair) => <div key={pair[0].section} className={`fd-row${pair.length > 1 ? ' fd-row--pair' : ''}`}>
                  {pair.map((spec) => <MonthlyTable key={spec.section} spec={spec} rows={rows} onSelection={onSelection}
                    totalsOverride={spec.section === 'rep' ? repTotals : undefined} />)}
                </div>)}
              </div>}
            </div>;
          })}
        </div>
        <SelectionBar totals={selection} /></>}
  </div>;
}
